import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconAutocomplete from "../../../../components/forms/IconAutocomplete";
import { COMMENT_TYPE_OPTIONS } from "../../../../lib/ui/iconSelectOptions";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { formatDateTime, formatRelativeFromNow } from "../../../../lib/datetime/displayDateTime";
import authService from "../../../../services/authService";
import { incidentService } from "../../../../services/incidentService";
import type { AddCommentRequest, Incident, IncidentComment } from "../../../../types/incident";

export type IncidentCommentsHandle = {
  startComment: () => void;
};

type IncidentCommentsProps = {
  incident: Incident;
  onAdded: () => void;
};

function initials(name?: string | null) {
  const value = name?.trim() || "?";
  const parts = value.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }
  return value.charAt(0).toUpperCase();
}

const IncidentComments = forwardRef<IncidentCommentsHandle, IncidentCommentsProps>(
  function IncidentComments({ incident, onAdded }, ref) {
    const { t } = useTranslation();
    const sectionRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const [expanded, setExpanded] = useState(false);
    const [draft, setDraft] = useState("");
    const [commentType, setCommentType] = useState<AddCommentRequest["type"]>("UPDATE");
    const [saving, setSaving] = useState(false);
    const [currentUserName, setCurrentUserName] = useState("");

    useEffect(() => {
      void authService.getUser().then((user) => {
        const name =
          user?.profile?.name ||
          user?.profile?.preferred_username ||
          user?.profile?.email ||
          "";
        setCurrentUserName(typeof name === "string" ? name : "");
      });
    }, []);

    useEffect(() => {
      setExpanded(false);
      setDraft("");
      setCommentType("UPDATE");
      setSaving(false);
    }, [incident.id]);

    const comments = useMemo(() => {
      return [...(incident.comments ?? [])].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
    }, [incident.comments]);

    const expandAndFocus = useCallback(() => {
      setExpanded(true);
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      window.setTimeout(() => inputRef.current?.focus(), 180);
    }, []);

    useImperativeHandle(ref, () => ({ startComment: expandAndFocus }), [expandAndFocus]);

    const handleCancel = () => {
      setExpanded(false);
      setDraft("");
      setCommentType("UPDATE");
    };

    const handleSave = async () => {
      const text = draft.trim();
      if (!text || saving) return;

      setSaving(true);
      try {
        await incidentService.addComment(incident.id, { comment: text, type: commentType });
        window.dispatchEvent(
          new CustomEvent("showGlobalNotification", {
            detail: {
              color: "success",
              icon: "success",
              title: t("incidents.commentRequest"),
              content: t("incidents.commentAdded"),
              dateTime: Date.now,
            },
          }),
        );
        setDraft("");
        setExpanded(false);
        setCommentType("UPDATE");
        onAdded();
      } catch (error) {
        console.error("Error adding comment:", error);
        window.dispatchEvent(
          new CustomEvent("showGlobalNotification", {
            detail: {
              color: "error",
              icon: "warning",
              title: t("incidents.commentRequest"),
              content: t("incidents.commentFailed"),
              dateTime: Date.now,
            },
          }),
        );
      } finally {
        setSaving(false);
      }
    };

    return (
      <Box
        ref={sectionRef}
        sx={{
          mt: 1,
          px: 2.5,
          pt: 2.5,
          pb: 3,
          borderTop: 1,
          borderColor: "divider",
          bgcolor: (theme) =>
            theme.palette.mode === "dark"
              ? alpha(theme.palette.common.white, 0.03)
              : alpha(theme.palette.common.black, 0.02),
        }}
      >
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{ fontSize: "1.05rem", letterSpacing: "-0.01em", mb: 0.25 }}
        >
          {t("incidents.commentsTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.25 }}>
          {t("incidents.commentsCount", { count: comments.length })}
        </Typography>

        <Stack spacing={0}>
          {comments.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("incidents.noCommentsHint")}
            </Typography>
          ) : (
            comments.map((comment, index) => (
              <CommentItem
                key={comment.id || `${comment.createdAt}-${comment.author}-${index}`}
                comment={comment}
                showDivider={index < comments.length - 1}
              />
            ))
          )}
        </Stack>

        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mt: comments.length ? 2 : 0 }}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              fontSize: "0.75rem",
              fontWeight: 700,
              bgcolor: "primary.main",
              mt: 0.25,
            }}
          >
            {initials(currentUserName || t("incidents.you"))}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {expanded ? (
              <>
                <TextField
                  inputRef={inputRef}
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  placeholder={t("incidents.writeAComment")}
                  multiline
                  minRows={3}
                  fullWidth
                  size="small"
                  onKeyDown={(event) => {
                    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                      event.preventDefault();
                      void handleSave();
                    }
                    if (event.key === "Escape") {
                      handleCancel();
                    }
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "background.paper",
                      borderRadius: 1,
                      fontSize: "0.875rem",
                      lineHeight: 1.6,
                    },
                  }}
                />
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ mt: 1 }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Box sx={{ minWidth: 180 }}>
                    <IconAutocomplete
                      options={COMMENT_TYPE_OPTIONS.map((option) => ({
                        ...option,
                        label: t(`incidents.commentTypes.${option.value}`),
                      }))}
                      value={commentType}
                      onChange={(value) => {
                        if (value) setCommentType(value as AddCommentRequest["type"]);
                      }}
                      disableClearable
                    />
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      color="inherit"
                      onClick={handleCancel}
                      disabled={saving}
                      sx={{ textTransform: "none" }}
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      disableElevation
                      onClick={() => void handleSave()}
                      disabled={!draft.trim() || saving}
                      sx={{ textTransform: "none", fontWeight: 700, px: 1.75 }}
                    >
                      {t("common.save")}
                    </Button>
                  </Stack>
                </Stack>
              </>
            ) : (
              <Box
                role="button"
                tabIndex={0}
                onClick={expandAndFocus}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    expandAndFocus();
                  }
                }}
                sx={{
                  minHeight: 40,
                  display: "flex",
                  alignItems: "center",
                  px: 1.5,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "background.paper",
                  color: "text.disabled",
                  cursor: "text",
                  fontSize: "0.875rem",
                  "&:hover": {
                    borderColor: "action.active",
                  },
                  "&:focus-visible": {
                    outline: "2px solid",
                    outlineColor: "primary.main",
                    outlineOffset: 1,
                  },
                }}
              >
                {t("incidents.writeAComment")}
              </Box>
            )}
          </Box>
        </Stack>
      </Box>
    );
  },
);

function CommentItem({
  comment,
  showDivider,
}: {
  comment: IncidentComment;
  showDivider: boolean;
}) {
  const { t } = useTranslation();
  const relative = formatRelativeFromNow(comment.createdAt);
  const absolute = formatDateTime(comment.createdAt);

  return (
    <Box
      sx={{
        py: 1.5,
        borderBottom: showDivider ? 1 : 0,
        borderColor: "divider",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Avatar
          sx={{
            width: 32,
            height: 32,
            fontSize: "0.75rem",
            fontWeight: 700,
            bgcolor: "primary.main",
          }}
        >
          {initials(comment.author)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0, pt: 0.15 }}>
          <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap" useFlexGap>
            <Typography variant="body2" fontWeight={700} sx={{ fontSize: "0.875rem" }}>
              {comment.author || "—"}
            </Typography>
            <Tooltip title={absolute || ""}>
              <Typography variant="caption" color="text.secondary">
                {relative}
              </Typography>
            </Tooltip>
            {comment.type ? (
              <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: "0.02em" }}>
                · {t(`incidents.commentTypes.${comment.type}`)}
              </Typography>
            ) : null}
          </Stack>
          <Typography
            variant="body2"
            sx={{
              mt: 0.5,
              lineHeight: 1.65,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              color: "text.primary",
            }}
          >
            {comment.comment}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

export default IncidentComments;
